'use client'

import React, { useState, useEffect } from 'react'
import { Button, InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from '@sarkiai/ui'
import { Lock, KeyRound, LogIn, ChevronRight, AlertCircle } from 'lucide-react'
import { motion } from 'framer-motion'
import { useTranslations } from '@/components/I18nProvider'
import { useAssistant } from './AssistantContext'
import Link from 'next/link'

export function AssistantLockScreen({ onDismiss }: { onDismiss?: () => void }) {
  const t = useTranslations('chat')
  const { unlockWithCode, quota } = useAssistant()
  const [code, setCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [countdown, setCountdown] = useState(20)

  // Auto-dismiss after 20 seconds
  useEffect(() => {
    if (countdown <= 0) {
      onDismiss?.()
      return
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [countdown, onDismiss])

  const handleUnlock = async () => {
    if (code.length < 6) return

    setIsSubmitting(true)
    setError(null)

    const result = await unlockWithCode(code)
    if (!result.success) {
      setError(result.message || t('lockscreen.invalid_code'))
      setIsSubmitting(false)
    } else {
      onDismiss?.()
    }
  }

  return (
    <div className="absolute inset-0 z-50 flex flex-col items-center justify-center p-6 bg-background/80 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-sm flex flex-col items-center text-center gap-6"
      >
        <div className="h-16 w-16 rounded-3xl bg-primary/10 flex items-center justify-center mb-2">
          <Lock className="h-8 w-8 text-primary" />
        </div>

        <div className="space-y-2">
          <h3 className="text-xl font-bold text-foreground">
            {t('lockscreen.title') || 'Quota atteint'}
          </h3>
          <p className="text-xs font-medium text-primary tabular-nums">
            {t('lockscreen.auto_dismiss', { count: countdown }) || `Fermeture automatique dans ${countdown}s`}
          </p>
          {quota?.resetAt && (
            <p className="text-[10px] text-muted-foreground/60 mt-1">
              {t('lockscreen.next_reset', {
                date: new Date(quota.resetAt).toLocaleString(undefined, {
                  weekday: 'long',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              }) || `Réinitialisation : ${new Date(quota.resetAt).toLocaleTimeString()}`}
            </p>
          )}
        </div>

        <div className="w-full space-y-4 pt-4">
          <div className="flex flex-col items-center gap-3">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {t('lockscreen.enter_code') || 'Code d\'accès'}
            </label>
            <InputOTP
              maxLength={6}
              value={code}
              onChange={setCode}
              disabled={isSubmitting}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
              </InputOTPGroup>
              <InputOTPSeparator />
              <InputOTPGroup>
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-1.5 text-xs text-destructive font-medium"
              >
                <AlertCircle className="h-3.5 w-3.5" />
                {error}
              </motion.div>
            )}

            <Button
              className="w-full mt-2 rounded-2xl"
              onClick={handleUnlock}
              disabled={code.length < 6 || isSubmitting}
            >
              <KeyRound className="h-4 w-4 mr-2" />
              {t('lockscreen.unlock') || 'Débloquer +20 messages'}
            </Button>
          </div>

          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border/50" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t('lockscreen.or') || 'Ou'}
              </span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full rounded-2xl border-primary/20 hover:bg-primary/5"
            asChild
          >
            <Link href="https://vcard.djotta.com/login" target='_blank'>
              <LogIn className="h-4 w-4 mr-2" />
              {t('lockscreen.login') || 'Se connecter / S\'inscrire'}
              <ChevronRight className="h-3 w-3 ml-auto opacity-50" />
            </Link>
          </Button>
        </div>
      </motion.div>
    </div>
  )
}
